import { api } from '@/integrations/clients/carAuction/carAuctionClient';
import {
  ListingOverview,
  ListingDetails,
  GetAllListingsResponse,
  GetListingResponse,
  GetMyListingsResponse,
  GetMyBiddingsParticipationResponse,
  ListingQueryParams,
  AuctionDetails,
  ListingMyOrder,
  ListingStatus,
  ListingType,
  ListingMyListings,
  ListingBiddingParticipation,
  Bid,
  BiddingMeta,
} from '@/types/listing';
import { Order } from '@/types/order';
import { PaymentService } from '@/services/payment/paymentService';
import { TransportService } from '@/services/transport/transportService';
import { TestService } from '../test/testService';
import type { AutoUncleRatingIntervals } from '@/types/valuation';



export class ListingService {
  // Get all listings with optional filters
  static async getAllListings(params: ListingQueryParams = {}): Promise<GetAllListingsResponse> {
      const queryParams = new URLSearchParams();
      if (params.page) queryParams.append('page', params.page.toString());
      if (params.limit) queryParams.append('limit', params.limit.toString());
      if (params.status) {
        // If status is array, join with comma; otherwise use as-is
        const statusValue = Array.isArray(params.status) ? params.status.join(',') : params.status;
        queryParams.append('status', statusValue);
      }
      if (params.listingType) queryParams.append('listingType', params.listingType);
      if (params.seller) queryParams.append('seller', params.seller);
      if (params.winner) queryParams.append('winner', params.winner);
      if (params.campaignId) queryParams.append('campaignId', params.campaignId);
      if (params.regNumber) queryParams.append('regNumber', params.regNumber);
      if (params.vin) queryParams.append('vin', params.vin);
      if (params.search) {
        // Backend accepts search as filters[search] or directly as search
        queryParams.append('filters[search]', params.search);
      }
      if (params.sort) queryParams.append('sort', params.sort);
      if (params.seed) queryParams.append('seed', params.seed);

      // Backend returns { success, count, pagination, data }
      // api.get returns response.data, so we get { success, count, pagination, data }
      const response = await api.get<{ success: boolean; count: number; pagination: any; data: ListingOverview[] }>(`/listings?${queryParams.toString()}`);
      
      // Map backend response to frontend GetAllListingsResponse format
      // Backend now returns total count in 'count' field (was fixed to use result.total)
      const totalCount = response.count || 0;
      const limit = params.limit || 10;
      const totalPages = Math.ceil(totalCount / limit);
      
      return {
        listings: response.data || [],
        data: response.data || [], // For backward compatibility
        totalCount: totalCount,
        totalPages: totalPages,
        page: params.page || 1,
        limit: limit,
      };
  }


  // Get a specific listing with VehicleTest (for CarDetails page)
  static async getListing(listingId: string, userId?: string): Promise<GetListingResponse> {
      const response = await api.get<GetListingResponse>(`/listings/${listingId}`);
      return response;
  }

  /** Guest-safe listing detail (no reg/VIN/sensitive fields in response). */
  static async getPublicListing(listingId: string): Promise<GetListingResponse> {
    const response = await api.get<GetListingResponse>(
      `/listings/public/${listingId}`,
    );
    return response;
  }

  /** Guest-safe listing list for /auctions browse. */
  static async getPublicListings(
    params: ListingQueryParams = {},
  ): Promise<GetAllListingsResponse> {
    const queryParams = new URLSearchParams();
    if (params.page) queryParams.append("page", params.page.toString());
    if (params.limit) queryParams.append("limit", params.limit.toString());
    if (params.search) queryParams.append("filters[search]", params.search);
    if (params.sort) queryParams.append("sort", params.sort);
    if (params.seed) queryParams.append("seed", params.seed);

    const response = await api.get<{
      success: boolean;
      count: number;
      data: ListingOverview[];
      listings: ListingOverview[];
      totalCount: number;
      totalPages: number;
      page: number;
      limit: number;
    }>(`/listings/public?${queryParams.toString()}`);

    return {
      listings: response.listings || response.data || [],
      data: response.data || response.listings || [],
      totalCount: response.totalCount ?? response.count ?? 0,
      totalPages: response.totalPages ?? 0,
      page: response.page ?? params.page ?? 1,
      limit: response.limit ?? params.limit ?? 10,
    };
  }

  /**
   * Fetch rating intervals for a listing — same backend flow as admin
   * POST /vehicle-cards/:id/rating-intervals/fetch when the listing has a vehicle card.
   * Used on CarDetails. Requires auth.
   */
  static async fetchListingRatingIntervals(listingId: string): Promise<AutoUncleRatingIntervals> {
    const response = await api.post<{ success: boolean; data: AutoUncleRatingIntervals }>(
      `/listings/${listingId}/rating-intervals/fetch`,
      {}
    );
    return response.data ?? {};
  }


  // Get my orders (listings where user is sellerId) with payment and transport data
  static async getMyOrders(params: Omit<ListingQueryParams, 'seller'> = {}): Promise<{ orders: Order[]; total: number; page: number; limit: number }> {
      // TODO: Backend implementation
      const queryParams = new URLSearchParams();
      if (params.page) queryParams.append('page', params.page.toString());
      if (params.limit) queryParams.append('limit', params.limit.toString());
      if (params.listingType) queryParams.append('listingType', params.listingType);
      if (params.campaignId) queryParams.append('campaignId', params.campaignId);
      if (params.regNumber) queryParams.append('regNumber', params.regNumber);
      if (params.vin) queryParams.append('vin', params.vin);
      if (params.deliveryStatus) queryParams.append('deliveryStatus', params.deliveryStatus);
      if (params.paymentStatus) queryParams.append('paymentStatus', params.paymentStatus);
      if (params.sort) queryParams.append('sort', params.sort);

      const response = await api.get<{ orders: Order[]; total: number; page: number; limit: number }>(`/listings/my-orders?${queryParams.toString()}`);
      return response;
  }

  // Get my listings (listings where user is sellerId) with bidding data
  static async getMyListings(params: Omit<ListingQueryParams, 'seller'> = {}): Promise<GetMyListingsResponse> {
      const queryParams = new URLSearchParams();
      if (params.page) queryParams.append('page', params.page.toString());
      if (params.limit) queryParams.append('limit', params.limit.toString());
      if (params.status) {
        const statusValue = Array.isArray(params.status) ? params.status.join(',') : params.status;
        queryParams.append('status', statusValue);
      }
      // Backend expects filters[listingType] for my-listings
      if (params.listingType) queryParams.append('filters[listingType]', params.listingType);
      if (params.campaignId) queryParams.append('campaignId', params.campaignId);
      if (params.regNumber) queryParams.append('regNumber', params.regNumber);
      if (params.vin) queryParams.append('vin', params.vin);

      const response = await api.get<GetMyListingsResponse>(`/listings/my-listings?${queryParams.toString()}`);
      return response;
  }

  // Get my biddings participation (auctions where user has bid)
  static async getMyBiddingsParticipation(params: Omit<ListingQueryParams, 'seller' | 'winner'> = {}): Promise<GetMyBiddingsParticipationResponse> {
      const queryParams = new URLSearchParams();
      if (params.page) queryParams.append('page', params.page.toString());
      if (params.limit) queryParams.append('limit', params.limit.toString());
      if (params.status) {
        const statusValue = Array.isArray(params.status) ? params.status.join(',') : params.status;
        queryParams.append('status', statusValue);
      }
      if (params.listingType) queryParams.append('listingType', params.listingType);
      if (params.campaignId) queryParams.append('campaignId', params.campaignId);

      const response = await api.get<GetMyBiddingsParticipationResponse>(`/listings/my-biddings-participation?${queryParams.toString()}`);
      return response;
  }

  // Hide auction from my biddings participation (marks bids as hidden)
  static async removeAuctionFromMyBiddingsParticipation(listingId: string): Promise<void> {
      await api.patch(`/listings/my-biddings-participation/${listingId}/hide`);
  }

  // Get listing bids
  static async getListingBids(listingId: string): Promise<{
    success: boolean;
    data: Array<{
      id: string;
      amount: number;
      bidFee: number;
      bidderId: string;
      createdAt: string;
      bidderName?: string;
      bidderEmail?: string;
    }>;
    favoriteCount?: number;
  }> {
    const response = await api.get<{
      success: boolean;
      data: Array<{
        id: string;
        amount: number;
        bidFee: number;
        bidderId: string;
        createdAt: string;
        bidderName?: string;
        bidderEmail?: string;
      }>;
      favoriteCount?: number;
    }>(`/listings/${listingId}/bids`);
    return response;
  }

  /**
   * Reserve a listing (Buy Now flow)
   * Updates listing status to RESERVED and triggers admin notification
   */
  static async reserveListing(listingId: string): Promise<{ success: boolean; listingId: string }> {
    
    const response = await api.patch<{ success: boolean; data: { success: boolean; listingId: string } }>(`/listings/${listingId}/reserve`);
    return response.data;
  }

  static async getAutomaticBid(listingId: string): Promise<{
    id: string;
    maxAmount: number;
    isActive: boolean;
    companyId: string | null;
    actorId: string;
    limitReachedAt: string | null;
  } | null> {
    const response = await api.get<{
      success: boolean;
      data: {
        id: string;
        maxAmount: number;
        isActive: boolean;
        companyId: string | null;
        actorId: string;
        limitReachedAt: string | null;
      } | null;
    }>(`/listings/${listingId}/automatic-bid`);
    return response.data ?? null;
  }

  static async setAutomaticBid(
    listingId: string,
    maxAmount: number,
  ): Promise<{
    autoBid: {
      id: string;
      maxAmount: number;
      isActive: boolean;
      companyId: string | null;
      actorId: string;
      limitReachedAt: string | null;
    };
    highestBid?: number;
    totalBids?: number;
  }> {
    const response = await api.put<{
      success: boolean;
      data: {
        autoBid: {
          id: string;
          maxAmount: number;
          isActive: boolean;
          companyId: string | null;
          actorId: string;
          limitReachedAt: string | null;
        };
        highestBid?: number;
        totalBids?: number;
      };
    }>(`/listings/${listingId}/automatic-bid`, { maxAmount });
    return response.data;
  }

  static async cancelAutomaticBid(listingId: string): Promise<{
    id: string;
    maxAmount: number;
    isActive: boolean;
    companyId: string | null;
    actorId: string;
    limitReachedAt: string | null;
  } | null> {
    const response = await api.delete<{
      success: boolean;
      data: {
        id: string;
        maxAmount: number;
        isActive: boolean;
        companyId: string | null;
        actorId: string;
        limitReachedAt: string | null;
      } | null;
    }>(`/listings/${listingId}/automatic-bid`);
    return response.data ?? null;
  }

  /** Ask Bilenia about this listing; email is queued server-side (EmailOutbox). */
  static async submitListingInquiry(listingId: string, message: string): Promise<void> {
    await api.post<{ success: boolean }>(`/listings/${listingId}/inquiry`, { message });
  }

  /**
   * Get public listings for carousel (unprotected endpoint)
   * Returns active listings ordered by popularity (unique views, views, created date)
   */
  static async getPublicListingsCarousel(limit: number = 10): Promise<ListingOverview[]> {
    const response = await api.get<{ success: boolean; data: Array<{
      id: string;
      title: string;
      type: string;
      status: string;
      views: number;
      uniqueViews: number;
      vehicle: {
        make: string;
        model: string;
        year: number | null;
        mileage: number | null;
        fuelType: string | null;
        transmission: string | null;
        regNumber: string;
      };
      auctionDetails?: {
        price: string;
        buyNowFixed: string | null;
        currentBuyNowPrice?: string | null;
      };
      directPurchaseDetails?: {
        currentPrice: string;
        originalPrice?: string;
      };
      thumbnailUrl: string;
      hoverThumbnailUrl?: string;
      campaign: {
        id: string;
        name: string;
      } | null;
    }> }>(`/listings/public/carousel?limit=${limit}`);

    // Map backend response to ListingOverview format
    return (response.data || []).map((item) => {
      // Backend returns "DIRECT_PURCHASE" or "AUCTION" (uppercase enum), map to frontend format
      const listingType: ListingType = item.type?.toUpperCase() === 'DIRECT_PURCHASE' ? 'direct-purchase' : 'auction';

      // Build auctionDetails or directPurchaseDetails based on backend response
      const auctionDetails = item.auctionDetails ? {
        minimumBidIncrement: 0, // Not provided by backend
        startingPrice: parseFloat(item.auctionDetails.price) || 0,
        buyNowFixed: item.auctionDetails.buyNowFixed ? parseFloat(item.auctionDetails.buyNowFixed) : undefined,
        currentBuyNowPrice: item.auctionDetails.currentBuyNowPrice
          ? parseFloat(item.auctionDetails.currentBuyNowPrice)
          : (item.auctionDetails.buyNowFixed ? parseFloat(item.auctionDetails.buyNowFixed) : undefined),
        reservationPrice: 0, // Not provided by backend
        currentBid: parseFloat(item.auctionDetails.price) || 0, // Use price as current bid
      } : undefined;

      const directPurchaseDetails = item.directPurchaseDetails ? {
        currentPrice: parseFloat(item.directPurchaseDetails.currentPrice) || 0,
        originalPrice: item.directPurchaseDetails.originalPrice ? parseFloat(item.directPurchaseDetails.originalPrice) : undefined,
      } : undefined;

      // Normalize status from backend (uppercase) to frontend format (lowercase)
      const normalizedStatus = (item.status?.toLowerCase() || 'active') as ListingStatus;
      
      return {
        id: item.id,
        listingType,
        title: item.title,
        description: '', // Not provided by backend
        startTime: new Date().toISOString(), // Not provided by backend, use current time
        endTime: new Date().toISOString(), // Not provided by backend, use current time
        status: normalizedStatus,
        createdById: '', // Not provided by backend
        sellerId: '', // Not provided by backend
        winnerId: undefined,
        thumbnail: item.thumbnailUrl,
        hoverThumbnail: item.hoverThumbnailUrl || undefined,
        createdAt: new Date().toISOString(), // Not provided by backend
        updatedAt: new Date().toISOString(), // Not provided by backend
        campaignId: item.campaign?.id,
        views: item.views || 0,
        vehicleTestId: '', // Not provided by backend
        vehicleDetails: {
          regNumber: item.vehicle.regNumber,
          make: item.vehicle.make,
          model: item.vehicle.model,
          year: item.vehicle.year || undefined,
          mileage: item.vehicle.mileage || undefined,
          fuelType: item.vehicle.fuelType || undefined,
          transmission: item.vehicle.transmission || undefined,
        },
        isFavorite: false, // Not provided by public endpoint
        auctionDetails,
        directPurchaseDetails,
      } as ListingOverview;
    });
  }
}
